/**
 * useVoiceVisualizerWrapper - react-voice-visualizer 包装器
 *
 * 核心功能：解决快速点击导致录音无法停止的竞态条件问题
 *
 * 🔥 关键原理：
 * 1. 使用 mediaRecorderRef.current 获取实时的 MediaRecorder 实例（不是解构的快照！）
 * 2. 监听 MediaRecorder.state === 'recording' 而不是 React 状态
 * ⚠️ 重要：不要改回解构方式获取 mediaRecorder，那样会拿到过期的快照值！
 */
import { useCallback, useRef, useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { useVoiceVisualizer } from 'react-voice-visualizer';
import { fetchApi } from '../../../lib/apiClient';
import { sttProcessingAtom } from '../../../atoms/v3/chatInputAtoms';
import { addToastAtom } from '../../../atoms/v0/uiAtoms';
import i18n from 'i18next';
import { useFrontendLogger } from '../../../atoms/v3/debugAtoms';

interface WrapperOptions {
  onStartRecording?: () => void;
  onStopRecording?: () => void;
  onErrorPlayingAudio?: (error: Error) => void;
  onQuickRelease?: () => void; // 🔥 新增：快速抬手回调
  onTranscriptionComplete?: (text: string) => void; // 🔥 新增：转录完成回调
  enableSTT?: boolean; // 🔥 新增：是否启用STT功能
  enableSTTRef?: React.MutableRefObject<boolean>; // 🔥 新增：动态控制STT的ref
}

export const useVoiceVisualizerWrapper = (
  options: WrapperOptions = {}
) => {
  const {
    onStartRecording,
    onStopRecording,
    onErrorPlayingAudio,
    onQuickRelease,
    onTranscriptionComplete,
    enableSTT = false,
    enableSTTRef
  } = options;
  const logger = useFrontendLogger();

  // STT状态管理
  const setSTTProcessing = useSetAtom(sttProcessingAtom);
  const addToast = useSetAtom(addToastAtom);

  // 最小状态管理
  const pendingStopRef = useRef(false);
  const hasStartedRecordingRef = useRef(false);
  const stateWatcherRef = useRef<NodeJS.Timeout | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null); // 🔥 关键：实时跟踪 MediaRecorder
  const isRecordingInProgressRef = useRef(false);
  const recorderControlsRef = useRef<any>(null); // 🔥 新增：实时跟踪 recorderControls
  const recordingStartTimeRef = useRef<number | null>(null); // 🔥 新增：记录录音开始时间

  // 🔥 语言映射表 - 将i18n语言代码映射到OpenAI STT支持的语言代码
  const getSTTLanguageCode = useCallback(() => {
    const currentLang = i18n.language.split('-')[0]; // 提取主要语言代码，例如 'zh-CN' -> 'zh'

    // OpenAI STT支持的语言代码映射
    const languageMap: Record<string, string> = {
      'zh': 'zh', // 中文
      'en': 'en', // 英文
      'ja': 'ja', // 日文
      'ko': 'ko', // 韩文
      'es': 'es', // 西班牙文
      'fr': 'fr', // 法文
      'de': 'de', // 德文
      'it': 'it', // 意大利文
      'pt': 'pt', // 葡萄牙文
      'ru': 'ru', // 俄文
      'ar': 'ar', // 阿拉伯文
      'hi': 'hi', // 印地文
      'th': 'th', // 泰文
      'vi': 'vi', // 越南文
    };

    return languageMap[currentLang] || 'zh'; // 默认使用中文
  }, []);

  // 🔥 新增：STT处理函数
  const processSTT = useCallback(async (audioBlob: Blob) => {
    if (!enableSTT || !onTranscriptionComplete) {
      return;
    }

    // 🔥 创建AbortController用于10秒超时控制
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      logger.warn('🔥 useVoiceVisualizerWrapper: STT请求超时，正在取消请求...');
      controller.abort();
    }, 10000); // 10秒超时

    try {
      // 🔥 设置STT处理状态为true
      setSTTProcessing(true);

      // 🔥 获取当前语言代码
      const languageCode = getSTTLanguageCode();

      // 🔥 根据实际的 MIME 类型确定文件扩展名
      const getFileExtension = (mimeType: string): string => {
        if (mimeType.includes('webm')) return 'webm';
        if (mimeType.includes('mp4')) return 'mp4';
        if (mimeType.includes('wav')) return 'wav';
        if (mimeType.includes('ogg')) return 'ogg';
        if (mimeType.includes('m4a')) return 'm4a';
        // 默认使用 webm，OpenAI 支持的格式
        return 'webm';
      };

      const fileExtension = getFileExtension(audioBlob.type);
      const fileName = `recording.${fileExtension}`;

      logger.info('🔥 useVoiceVisualizerWrapper: 开始STT处理', {
        audioSize: audioBlob.size,
        audioType: audioBlob.type,
        fileName: fileName,
        fileExtension: fileExtension,
        language: languageCode,
        currentI18nLang: i18n.language,
        // 🔥 添加更多调试信息
        audioBlobConstructor: audioBlob.constructor.name,
        isValidBlob: audioBlob instanceof Blob,
        blobStream: typeof audioBlob.stream === 'function' ? 'available' : 'not available'
      });

      // 创建FormData
      const formData = new FormData();
      const audioFile = new File([audioBlob], fileName, { type: audioBlob.type });
      formData.append('file', audioFile);
      // 🔥 添加语言参数
      formData.append('language', languageCode);

      // 🔥 发送到新的STT API，添加超时控制
      const response = await fetchApi<{ text: string; model: string; prompt_used: string }>('/stt/transcribe', {
        method: 'POST',
        body: formData,
        signal: controller.signal // 🔥 添加超时信号
      });

      // 🔥 清除超时计时器
      clearTimeout(timeoutId);

      logger.info('🔥 useVoiceVisualizerWrapper: STT转录成功', {
        text: response.text,
        model: response.model,
        textLength: response.text?.length || 0,
        language: languageCode
      });

      // 调用回调函数
      onTranscriptionComplete(response.text);
    } catch (error: any) {
      // 🔥 清除超时计时器
      clearTimeout(timeoutId);

      // 🔥 检查是否是超时错误（AbortError被fetchApi包装了）
      if (error.name === 'AbortError' || error.message?.includes('signal is aborted')) {
        logger.error('🔥 useVoiceVisualizerWrapper: STT转录超时（10秒）');
        throw new Error('转录失败：处理时间过长，请重试');
      }

      logger.error('🔥 useVoiceVisualizerWrapper: STT处理失败', error);
      throw error; // 🔥 重新抛出其他错误
    } finally {
      // 🔥 无论成功失败都重置STT处理状态
      setSTTProcessing(false);
    }
  }, [enableSTT, onTranscriptionComplete, setSTTProcessing, getSTTLanguageCode, addToast]);

  // 清理函数
  const cleanup = useCallback(() => {
    pendingStopRef.current = false;
    hasStartedRecordingRef.current = false;
    if (stateWatcherRef.current) {
      clearInterval(stateWatcherRef.current);
      stateWatcherRef.current = null;
    }
  }, []);

  // 使用原始 hook
  const recorderControls = useVoiceVisualizer({
    onStartRecording: () => {
      logger.info('🔥 useVoiceVisualizerWrapper: onStartRecording 回调被触发！');
      onStartRecording?.();
    },
    onStopRecording: () => {
      logger.info('🔥 useVoiceVisualizerWrapper: onStopRecording 回调被触发！');

      // 🔥 录音停止后，如果启用STT且有录音数据，使用轮询等待recordedBlob生成
      const waitForRecordedBlob = async () => {
        // 🔥 优先检查enableSTTRef，如果没有则使用enableSTT
        const shouldEnableSTT = enableSTTRef ? enableSTTRef.current : enableSTT;

        logger.info('🔥 useVoiceVisualizerWrapper: shouldEnableSTT', shouldEnableSTT);

        if (!shouldEnableSTT) {
          logger.info('🔥 useVoiceVisualizerWrapper: STT被禁用，跳过处理');
          return;
        }

        // 🔥 轮询等待recordedBlob生成，移动端可能需要更长时间
        let attempts = 0;
        const maxAttempts = 50; // 50次 * 100ms = 5秒，移动端需要更长等待时间

        const checkBlob = async () => {
          attempts++;
          const currentBlob = recorderControlsRef.current?.recordedBlob;

          // 🔥 每5次尝试输出一次日志，避免日志过多
          if (attempts % 5 === 1 || currentBlob) {
            logger.info(`🔥 useVoiceVisualizerWrapper: 检查recordedBlob (尝试 ${attempts}/${maxAttempts})`, {
              hasBlob: !!currentBlob,
              blobSize: currentBlob?.size || 0,
              blobType: currentBlob?.type || 'unknown',
              mediaRecorderState: mediaRecorderRef.current?.state || 'unknown'
            });
          }

          if (currentBlob && currentBlob.size > 0) {
            logger.info('🔥 useVoiceVisualizerWrapper: 录音停止，开始STT处理', {
              finalBlobSize: currentBlob.size,
              finalBlobType: currentBlob.type,
              waitTime: attempts * 100
            });
            try {
              await processSTT(currentBlob);
            } catch (error) {
              logger.error('🔥 useVoiceVisualizerWrapper: STT处理失败，但不影响录音流程', error);
              // 🔥 STT失败不影响录音本身的完成，只是没有转录文本
              // 显示用户友好的错误提示
              addToast({
                title: '语音转文字失败',
                description: error instanceof Error ? error.message : '转录服务暂时不可用，请重试',
                status: 'error',
                duration: 4000,
                isClosable: true,
              });
            }
            return;
          }

          if (attempts < maxAttempts) {
            // 🔥 移动端使用渐进式延迟：前20次100ms，后面200ms
            const delay = attempts <= 20 ? 100 : 200;
            setTimeout(checkBlob, delay);
          } else {
            logger.warn('🔥 useVoiceVisualizerWrapper: 等待recordedBlob超时', {
              totalWaitTime: '5秒',
              mediaRecorderState: mediaRecorderRef.current?.state,
              possibleCause: '录音时间过短、移动端处理延迟或录音失败'
            });
            // 🔥 不显示错误提示，因为可能是正常的快速录音
          }
        };

        // 🔥 开始检查
        setTimeout(checkBlob, 100);
      };

      // 🔥 异步等待recordedBlob
      waitForRecordedBlob();

      cleanup();
      onStopRecording?.();
    },
    onErrorPlayingAudio: (error: Error) => {
      console.log('🔥 useVoiceVisualizerWrapper: onErrorPlayingAudio 回调被触发！', error);
      cleanup();
      onErrorPlayingAudio?.(error);
    }
  });

  // 🔥 性能优化：不再解构，改为使用 ref 跟踪最新的 recorderControls
  // 避免解构导致的过期引用问题，确保所有调用都使用最新值

  // 🔥 关键修复：使用 ref 跟踪所有需要的实时值
  useEffect(() => {
    recorderControlsRef.current = recorderControls;
    mediaRecorderRef.current = recorderControls.mediaRecorder;
    isRecordingInProgressRef.current = recorderControls.isRecordingInProgress;
  }, [recorderControls, logger]);

  // 🔥 移除：不要监听recordedBlob变化，避免重复请求

  // 🔥 组件卸载时的清理逻辑
  useEffect(() => {
    return () => {
      console.log('🔥 useVoiceVisualizerWrapper: 组件卸载，开始清理资源');

      // 清理定时器
      if (stateWatcherRef.current) {
        clearInterval(stateWatcherRef.current);
        stateWatcherRef.current = null;
      }

      // 强制停止录音和清理MediaRecorder
      const currentMediaRecorder = mediaRecorderRef.current;
      if (currentMediaRecorder && currentMediaRecorder.state !== 'inactive') {
        console.log('🔥 useVoiceVisualizerWrapper: 强制停止MediaRecorder');
        try {
          currentMediaRecorder.stop();
        } catch (error) {
          console.warn('🔥 useVoiceVisualizerWrapper: MediaRecorder停止失败', error);
        }
      }

      // 清理所有状态
      cleanup();

      console.log('🔥 useVoiceVisualizerWrapper: 资源清理完成');
    };
  }, [cleanup]);

  // 智能开始录音
  const startRecording = useCallback(() => {
    cleanup(); // 清理之前的状态
    hasStartedRecordingRef.current = true; // 标记已开始录音流程
    recordingStartTimeRef.current = Date.now(); // 🔥 记录开始时间
    // 🔥 使用 ref 获取最新的 startRecording 函数
    recorderControlsRef.current?.startRecording();
  }, []);




  // 🔥 智能停止录音 - 解决快速点击竞态条件的核心逻辑
  const stopRecording = useCallback(() => {
    // console.log(`stop recording called`);
    // ⚠️ 关键：使用 mediaRecorderRef.current 获取实时值，不是解构的快照！
    const currentMediaRecorder = mediaRecorderRef.current;

    // 情况1：如果已经在录音，立即停止
    if (currentMediaRecorder?.state === 'recording' && isRecordingInProgressRef.current) {
      // 🔥 检查录音时长，如果 <1s 也触发快速抬手回调
      const recordingDuration = recordingStartTimeRef.current ? Date.now() - recordingStartTimeRef.current : 0;
      if (recordingDuration < 1000) {
        logger.info('🔥 Recording duration < 1s, triggering onQuickRelease');
        onQuickRelease?.();
      }

      // 🔥 组合调用：库状态管理 + 底层强制停止
      // console.log(`stop recording in recording`)
      // 🔥 使用 ref 获取最新的 stopRecording 函数
      recorderControlsRef.current?.stopRecording(); // 库的状态管理、UI更新、回调触发
      // currentMediaRecorder.stop(); // 底层MediaRecorder强制停止，确保音频流释放
      return;
    }

    // 情况2：如果已经调用了 startRecording 但录音还没开始，设置监听器等待
    if (hasStartedRecordingRef.current) {
      // console.log(`stop recording when not start`)
      pendingStopRef.current = true;

      // 🔥 核心逻辑：监听 MediaRecorder.state 变为 'recording'，然后立即停止
      stateWatcherRef.current = setInterval(() => {

        const currentMediaRecorder = mediaRecorderRef.current;

        // 关键条件：MediaRecorder 状态变为 'recording'
        if (pendingStopRef.current && currentMediaRecorder?.state === 'recording' && isRecordingInProgressRef.current) {
          // console.log(`trigger stop recording in interval`)
          // 清理监听器
          if (stateWatcherRef.current) {
            clearInterval(stateWatcherRef.current);
            stateWatcherRef.current = null;
          }

          // 重置标志
          pendingStopRef.current = false;
          hasStartedRecordingRef.current = false;

          onQuickRelease?.();

          // 🔥 组合调用：库状态管理 + 底层强制停止
          // 使用 ref 获取最新的 stopRecording 函数
          recorderControlsRef.current?.stopRecording(); // 库的状态管理、UI更新、回调触发
          // currentMediaRecorder.stop(); // 底层MediaRecorder强制停止，确保音频流释放
        }
      }, 50);

      // 超时保护：避免无限等待
      setTimeout(() => {
        if (pendingStopRef.current && stateWatcherRef.current) {
          clearInterval(stateWatcherRef.current);
          stateWatcherRef.current = null;
          pendingStopRef.current = false;
          hasStartedRecordingRef.current = false;
        }
      }, 5000);
    }
  }, []); // 🔥 移除依赖项，因为现在使用 ref

  // 🔥 更简洁的解决方案：直接返回原始对象，但覆盖关键方法
  // 这样可以确保所有属性都存在，避免 "Cannot read properties of undefined" 错误
  return {
    ...(recorderControlsRef.current || recorderControls),  // 🔥 展开所有原始属性，fallback 到当前值
    startRecording,  // 覆盖为我们的智能版本
    stopRecording,    // 覆盖为我们的智能版本
  };
};
